import { useState, ChangeEvent, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, RotateCcw } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const SAMPLE_DUPLICATES_TEXT = `Apple
Banana
Orange
Apple
Grape
Banana
Watermelon
Orange`;

export function DuplicateLineRemover() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ original: 0, removed: 0, remaining: 0 });

  const processText = (text: string, caseInsensitive: boolean) => {
    if (!text) {
      setOutput("");
      setStats({ original: 0, removed: 0, remaining: 0 });
      return;
    }

    const lines = text.split("\n");
    const seen = new Set<string>();
    const uniqueLines: string[] = [];

    for (const line of lines) {
      const key = caseInsensitive ? line.toLowerCase() : line;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueLines.push(line);
      }
    }

    setOutput(uniqueLines.join("\n"));
    setStats({
      original: lines.length,
      removed: lines.length - uniqueLines.length,
      remaining: uniqueLines.length
    });
  };

  useEffect(() => {
    setInput(SAMPLE_DUPLICATES_TEXT);
    processText(SAMPLE_DUPLICATES_TEXT, ignoreCase);
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    processText(val, ignoreCase);
  };

  const toggleIgnoreCase = (checked: boolean) => {
    setIgnoreCase(checked);
    processText(input, checked);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Duplicate Line Remover Online"
      description="Easily remove duplicate lines from your text. Clean your lists and data instantly with our free online tool."
      h1="Duplicate Line Remover"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your text with duplicates into the input area.",
        "Check 'Ignore Case' if you want case-insensitive deduplication.",
        "See the unique lines automatically in the output area."
      ]}
      faq={[
        { question: "How to remove duplicate lines from text?", answer: "Paste your text into our duplicate line remover tool and it will automatically generate a unique list." },
        { question: "Does this tool work with large text files?", answer: "Yes, it handles large lists efficiently in your browser." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 px-3 py-2 rounded-md hover:bg-slate-100 transition-colors">
            <input 
              type="checkbox" 
              className="accent-blue-600 w-4 h-4"
              checked={ignoreCase}
              onChange={(e) => toggleIgnoreCase(e.target.checked)}
            />
            <span className="text-sm font-medium text-slate-700">Ignore Case</span>
          </label>
          
          {stats.original > 0 && (
            <div className="text-sm text-slate-500">
              Removed <span className="font-bold text-slate-900">{stats.removed}</span> duplicates. 
              Remaining: <span className="font-bold text-slate-900">{stats.remaining}</span> lines.
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Original Text</label>
            <Textarea
              placeholder="Paste text here with duplicate lines..."
              className="min-h-[300px] whitespace-pre"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Unique Lines Only</label>
            <Textarea
              className="min-h-[300px] bg-slate-50 whitespace-pre"
              value={output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button 
            onClick={() => { 
              setInput(SAMPLE_DUPLICATES_TEXT); 
              processText(SAMPLE_DUPLICATES_TEXT, ignoreCase); 
            }} 
            variant="outline" 
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); setStats({original:0, removed:0, remaining:0}); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
